import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@foundation/components/ui/dialog";
import { Button } from "@foundation/components/ui/button";
import { Input } from "@foundation/components/ui/input";
import { Label } from "@foundation/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@foundation/components/ui/select";
import type { ClientRecord, ClientFieldsPayload } from "../types/clientTypes";

type ClientFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: ClientFieldsPayload) => Promise<void>;
  client?: ClientRecord | null;
  isSubmitting: boolean;
};

const EMPTY: ClientFieldsPayload = {
  client_name: "",
  email: "",
  phone: "",
  address: "",
  status: "Active",
};

export const ClientFormDialog = ({
  open,
  onOpenChange,
  onSubmit,
  client,
  isSubmitting,
}: ClientFormDialogProps) => {
  const [form, setForm] = useState<ClientFieldsPayload>(EMPTY);
  const isEdit = !!client;

  useEffect(() => {
    if (open) {
      setForm(
        client
          ? {
              client_name: client.client_name,
              email: client.email ?? "",
              phone: client.phone ?? "",
              address: client.address ?? "",
              status: client.status,
            }
          : EMPTY,
      );
    }
  }, [open, client]);

  const set = (key: keyof ClientFieldsPayload, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: ClientFieldsPayload = {
      client_name: form.client_name.trim(),
      email: form.email?.trim() || null,
      phone: form.phone?.trim() || null,
      address: form.address?.trim() || null,
      status: form.status,
    };
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Client" : "Add Client"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="client_name">Client Name *</Label>
            <Input
              id="client_name"
              value={form.client_name}
              onChange={(e) => set("client_name", e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email ?? ""}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone ?? ""}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={form.address ?? ""}
              onChange={(e) => set("address", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={form.status} onValueChange={(val) => set("status", val)}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? isEdit ? "Updating..." : "Creating..."
                : isEdit ? "Update Client" : "Add Client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
